import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Card, FormControlLabel, IconButton, InputAdornment, Switch, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox/index";
import MDTypography from "@/components/atoms/MDTypography";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import { ProductFormMessages } from "@/messages/products/product-form.messages";

type Props = {
  messages: ProductFormMessages;
  isEditing?: boolean;
};

function ProductInfo({ messages, isEditing = false }: Props) {
  const { values, handleChange, errors } = useFormikContext<ProductFormData>();

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight={"bold"}>
            {messages.productInformationTitle}
          </MDTypography>

          <FormControlLabel
            label={values.active ? messages.activeLabel : messages.inactiveLabel}
            control={<Switch name="active" checked={!!values.active} onChange={handleChange} />}
          />
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <FormField
                variant="standard"
                name="code"
                label={messages.codeLabel}
                value={values.code}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.code}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <FormField
                variant="standard"
                name="title"
                label={messages.titleLabel}
                value={values.title}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.title}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 3 }}>
              <FormField
                variant="standard"
                name="brand"
                label={messages.brandLabel}
                value={values.brand}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.brand}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                externalLabel
                name="description"
                label={messages.descriptionLabel}
                value={values.description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={7}
                error={!!errors.description}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormField
                externalLabel
                name="shortDescription"
                label={messages.shortDescriptionLabel}
                value={values.shortDescription}
                onChange={handleChange}
                fullWidth
                multiline
                rows={7}
                error={!!errors.shortDescription}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ mt: -2 }}>
              <MDTypography
                variant="overline"
                color={values.description.length > 4096 ? "error" : "inherit"}
              >
                <span>
                  <strong>{4096 - values.description.length}</strong>&nbsp;
                  {messages.charactersRemaining}
                </span>
              </MDTypography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ mt: -2 }}>
              <MDTypography
                variant="overline"
                color={values.shortDescription.length > 500 ? "error" : "inherit"}
              >
                <span>
                  <strong>{500 - values.shortDescription.length}</strong>&nbsp;
                  {messages.charactersRemaining}
                </span>
              </MDTypography>
            </Grid>
            <Grid size={12}>
              <FormField
                variant="standard"
                name="videoUrl"
                label={messages.videoUrlLabel}
                value={values.videoUrl || ""}
                onChange={handleChange}
                fullWidth
                autoComplete="off"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 1000 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={messages.videoUrlTooltip} arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default ProductInfo;
